import { localized, msg, str } from '@lit/localize';
import { css, html, LitElement, nothing, type PropertyValues } from 'lit';
import { customElement, property, query, state } from 'lit/decorators.js';
import type {
  Id,
  WorkflowAuditStatus,
  WorkflowAuditSummary,
  WorkflowSummary,
} from '../../../api/types';

import '@spectrum-web-components/action-button/sp-action-button.js';
import '@spectrum-web-components/action-menu/sp-action-menu.js';
import '@spectrum-web-components/menu/sp-menu-item.js';
import '@spectrum-web-components/progress-circle/sp-progress-circle.js';
import '@spectrum-web-components/icons-workflow/icons/sp-icon-play.js';
import '@spectrum-web-components/icons-workflow/icons/sp-icon-cancel.js';
import '@spectrum-web-components/icons-workflow/icons/sp-icon-delete.js';
import '@spectrum-web-components/icons-workflow/icons/sp-icon-history.js';
import '@spectrum-web-components/icons-workflow/icons/sp-icon-edit.js';
import '@spectrum-web-components/icons-workflow/icons/sp-icon-add.js';
import '@spectrum-web-components/search/sp-search.js';
import '@spectrum-web-components/button/sp-button.js';
import '@spectrum-web-components/table/sp-table.js';
import '@spectrum-web-components/table/sp-table-body.js';
import '@spectrum-web-components/table/sp-table-cell.js';
import '@spectrum-web-components/table/sp-table-head.js';
import '@spectrum-web-components/table/sp-table-head-cell.js';
import '@spectrum-web-components/table/sp-table-row.js';
import '../../../components/confirmation-dialog';
import { emitToast } from '../../../components/toast-events';
import '../../audit';
import { getLocale } from '../../../i18n/localization';
import { auditService } from '../../../services/audit-service';
import { sharedUiStyles } from '../../../styles/shared-ui.styles';
import {
  formatDateTime,
  formatDuration,
  formatNaturalDate as formatNaturalDateValue,
} from '../../../utils/format';
import type { WorkflowUserInputRequest } from '../dialogs/types';
import {
  type OverviewInteractionActionDetail,
  renderWorkflowOverviewUserInputActions,
  renderWorkflowOverviewUserInputPanel,
} from './workflow-overview-user-input-row.js';

type WorkflowSortKey = 'name' | 'lastExecutedAt';
type SortDirection = 'asc' | 'desc';
type OverviewSection =
  | 'overview'
  | 'needsAttention'
  | 'available'
  | 'templates'
  | 'history';
type WorkflowResetAction = 'stop' | 'dismiss';
type PendingWorkflowReset = {
  workflow: WorkflowSummary;
};

const WORKFLOW_OVERVIEW_SORT_STORAGE_KEY = 'kv.workflowOverviewSort';
const WORKFLOW_OVERVIEW_SECTION_STORAGE_KEY = 'kv.workflowOverviewSection';
const MOBILE_SORT_VALUE_SEP = '::';
const OVERVIEW_SECTIONS: Array<{
  key: OverviewSection;
}> = [
  { key: 'overview' },
  { key: 'needsAttention' },
  { key: 'available' },
  { key: 'templates' },
  { key: 'history' },
];

function isWorkflowSortKey(value: unknown): value is WorkflowSortKey {
  return value === 'name' || value === 'lastExecutedAt';
}

function isSortDirection(value: unknown): value is SortDirection {
  return value === 'asc' || value === 'desc';
}

function isOverviewSection(value: unknown): value is OverviewSection {
  return OVERVIEW_SECTIONS.some((section) => section.key === value);
}

function loadPersistedWorkflowOverviewSort(): {
  sortKey: WorkflowSortKey;
  sortDirection: SortDirection;
} {
  const fallback = {
    sortKey: 'name' as const,
    sortDirection: 'asc' as const,
  };
  try {
    const raw = localStorage.getItem(WORKFLOW_OVERVIEW_SORT_STORAGE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw) as {
        sortKey?: unknown;
        sortDirection?: unknown;
      };
      if (
        isWorkflowSortKey(parsed.sortKey) &&
        isSortDirection(parsed.sortDirection)
      ) {
        return { sortKey: parsed.sortKey, sortDirection: parsed.sortDirection };
      }
    }
  } catch {
    /* no-op */
  }
  return fallback;
}

function loadPersistedWorkflowOverviewSection(): OverviewSection {
  try {
    const raw = localStorage.getItem(WORKFLOW_OVERVIEW_SECTION_STORAGE_KEY);
    if (isOverviewSection(raw)) {
      return raw;
    }
  } catch {
    /* no-op */
  }
  return 'overview';
}

@customElement('kv-workflow-overview')
@localized()
export class KvWorkflowOverview extends LitElement {
  static styles = [
    sharedUiStyles,
    css`
    :host {
      display: block;
      height: 100%;
      color: var(--color-app-text, #1f1f1f);
      --workflow-surface: var(--kv-surface);
      --workflow-surface-muted: var(--kv-surface-muted);
      --workflow-surface-hover: var(--kv-surface-hover);
      --workflow-border: var(--kv-border);
      --workflow-accent: var(--kv-accent);
      --workflow-accent-bg: var(--kv-accent-bg);
      --workflow-shadow: var(--kv-shadow);
      --workflow-text-strong: var(--color-app-text);
      --workflow-text-muted: var(--color-app-text-secondary);
      --workflow-detail-divider: var(--workflow-border);
      --workflow-row-hover: var(--workflow-surface-hover);
      --workflow-row-selected: var(--workflow-accent-bg);
      --workflow-raw-error-bg: var(--workflow-surface-muted);
      --workflow-raw-error-text: var(--workflow-text-muted);
      --workflow-search-bg: var(--workflow-surface-muted);
      --workflow-search-border: var(--workflow-border);
      --workflow-search-border-hover: var(--color-accent-border-strong);
      --workflow-page-bg: #f6f7f9;
    }
    :host-context(sp-theme[color='dark']) {
      --workflow-page-bg: var(--color-app-bg, #1f1f1f);
      --workflow-shadow: none;
      --workflow-row-hover: rgba(255, 255, 255, 0.06);
      --workflow-row-selected: rgba(138, 164, 255, 0.22);
      --workflow-raw-error-bg: rgba(255, 255, 255, 0.04);
      --workflow-search-bg: rgba(255, 255, 255, 0.08);
      --workflow-search-border: rgba(255, 255, 255, 0.22);
      --workflow-search-border-hover: rgba(255, 255, 255, 0.34);
    }
    .overview {
      background: var(--workflow-page-bg);
      box-sizing: border-box;
      display: flex;
      flex-direction: column;
      height: 100%;
      min-height: 100vh;
      min-width: 0;
      overflow: auto;
      padding: 40px 48px;
    }
    .overview-shell {
      display: flex;
      flex-direction: column;
      gap: 0;
      margin: 0 auto;
      max-width: 1280px;
      min-height: 100%;
      min-width: 0;
      width: min(100%, 1280px);
    }
    .overview-header {
      align-items: center;
      display: flex;
      gap: 16px;
      justify-content: space-between;
      margin-bottom: 24px;
    }
    .overview-title {
      color: var(--color-app-text, #1f1f1f);
      font-size: 24px;
      font-weight: 750;
      line-height: 1.15;
      margin: 0;
    }
    .overview-description {
      color: var(--color-app-text-secondary, var(--workflow-text-muted));
      font-size: 13px;
      line-height: 1.45;
      margin: 6px 0 0;
    }
    .overview-actions {
      align-items: center;
      display: flex;
      gap: 8px;
    }
    .overview-sections {
      align-items: stretch;
      display: flex;
      flex: 0 0 auto;
      gap: 12px;
      min-height: 56px;
      overflow: hidden;
      padding: 0 12px;
      position: relative;
    }
    .overview-sections::after {
      background: var(--workflow-border);
      bottom: 0;
      content: '';
      height: 1px;
      left: 0;
      pointer-events: none;
      position: absolute;
      right: 0;
      z-index: 0;
    }
    .overview-section-sort {
      align-self: center;
      flex: 0 0 auto;
      margin-left: auto;
      padding-bottom: 0;
      position: relative;
      z-index: 1;
    }
    .overview-section-filter {
      align-self: center;
      flex: 0 1 auto;
      margin-left: auto;
      min-width: 0;
      padding-bottom: 0;
      position: relative;
      z-index: 1;
    }
    .overview-section-search {
      align-self: center;
      flex: 1 1 360px;
      margin-left: 0;
      max-width: 360px;
      min-width: 160px;
      padding-bottom: 0;
      position: relative;
      z-index: 1;
    }
    .overview-section-search .workflow-search {
      flex: 1 1 auto;
      width: 100%;
    }
    .overview-tab-list + .overview-section-search,
    .overview-section-filter + .overview-section-search {
      margin-left: auto;
    }
    .overview-tab-list {
      align-items: flex-end;
      align-self: flex-end;
      display: flex;
      flex: 0 1 auto;
      min-width: 0;
      position: relative;
      z-index: 1;
    }
    .overview-section-tab {
      align-items: flex-end;
      appearance: none;
      background: transparent;
      border: 0;
      border-bottom: 2px solid transparent;
      color: var(--color-app-text-secondary);
      cursor: pointer;
      display: inline-flex;
      font-size: 13px;
      font-weight: 700;
      height: 56px;
      margin: 0;
      padding: 0 10px 13px;
      position: relative;
      white-space: nowrap;
      z-index: 1;
    }
    .overview-section-tab:disabled {
      cursor: default;
      color: var(--color-app-text-secondary);
      opacity: 0.45;
    }
    .overview-section-tab[aria-selected='true'] {
      border-bottom-color: var(--workflow-accent);
      color: var(--workflow-accent);
    }
    .overview-section-tab:not(:disabled):hover {
      color: var(--workflow-text-strong);
    }
    .overview-section-tab:focus-visible {
      outline: 2px solid var(--workflow-accent);
      outline-offset: -4px;
    }
    .overview-section-tab-content {
      align-items: center;
      display: inline-flex;
      gap: 7px;
      min-height: 22px;
    }
    .overview-tab-count {
      align-items: center;
      background: var(--workflow-surface-muted);
      border: 1px solid var(--workflow-border);
      border-radius: 999px;
      color: var(--workflow-text-muted);
      display: inline-flex;
      font-size: 11px;
      font-weight: 750;
      line-height: 1;
      min-width: 18px;
      padding: 3px 6px;
      justify-content: center;
    }
    .history-wrap {
      flex: 1 1 auto;
      min-height: 0;
      overflow: hidden;
    }
    .history-wrap kv-module-audit {
      height: 100%;
      padding: 0;
    }
    .automation-overview {
      display: flex;
      flex: 1 1 auto;
      flex-direction: column;
      gap: 18px;
      min-height: 0;
      overflow: auto;
      padding: 0 20px 20px;
    }
    .automation-overview-panel {
      padding: 0;
    }
    .automation-cockpit-card {
      align-items: center;
      background: url('/img/automation-cockpit-light.webp') center / cover;
      border-bottom: 1px solid var(--workflow-border);
      display: grid;
      gap: 32px;
      grid-template-columns: minmax(260px, 0.85fr) minmax(360px, 1fr);
      margin: 0 -20px;
      min-height: 190px;
      overflow: hidden;
      padding: 28px;
    }
    :host-context(sp-theme[color='dark']) .automation-cockpit-card {
      background: url('/img/automation-cockpit-dark.webp') center / cover;
    }
    .automation-cockpit-copy {
      max-width: 420px;
      min-width: 0;
    }
    .automation-cockpit-support {
      display: flex;
      flex-direction: column;
      gap: 14px;
      justify-self: end;
      max-width: 620px;
      min-width: 0;
      width: 100%;
    }
    .automation-cockpit-text-panel {
      background: rgba(255, 255, 255, 0.76);
      border: 1px solid rgba(255, 255, 255, 0.62);
      border-radius: var(--kv-radius-panel);
      box-shadow: 0 14px 34px rgba(15, 23, 42, 0.08);
    }
    .automation-cockpit-text-panel {
      max-width: 620px;
      padding: 14px 16px;
    }
    :host-context(sp-theme[color='dark']) .automation-cockpit-text-panel {
      background: rgba(31, 31, 31, 0.72);
      border-color: rgba(255, 255, 255, 0.1);
      box-shadow: 0 14px 34px rgba(0, 0, 0, 0.2);
    }
    .automation-cockpit-title {
      color: var(--workflow-text-strong);
      font-size: 20px;
      font-weight: 750;
      line-height: 1.25;
      margin: 0;
    }
    .automation-cockpit-summary {
      color: var(--workflow-text-strong);
      font-size: 14px;
      font-weight: 650;
      line-height: 1.5;
      margin: 12px 0 0;
    }
    .automation-cockpit-message,
    .automation-cockpit-checked {
      color: var(--workflow-text-muted);
      font-size: 13px;
      line-height: 1.5;
      margin: 10px 0 0;
      max-width: 640px;
    }
    .automation-cockpit-message:first-child {
      margin-top: 0;
    }
    .automation-cockpit-checked {
      align-items: center;
      display: inline-flex;
      gap: 8px;
      margin-top: 10px;
    }
    .automation-cockpit-checked::before {
      background: #16a34a;
      border-radius: 999px;
      box-shadow: 0 0 0 3px rgba(22, 163, 74, 0.12);
      content: '';
      display: inline-block;
      height: 6px;
      width: 6px;
    }
    .automation-cockpit-badge {
      background: #fffbeb;
      border: 1px solid #fde68a;
      border-radius: 999px;
      color: #92400e;
      display: inline-flex;
      font-size: 12px;
      font-weight: 750;
      line-height: 1;
      margin-left: 8px;
      padding: 5px 9px;
      vertical-align: middle;
      white-space: nowrap;
    }
    .automation-cockpit-actions {
      align-items: flex-start;
      display: flex;
      flex: 0 0 auto;
      flex-direction: column;
      gap: 12px;
      justify-content: flex-start;
      margin-top: 16px;
      padding: 0;
    }
    .automation-cockpit-review-button {
      background: var(--workflow-accent);
      border: 1px solid var(--workflow-accent);
      border-radius: 8px;
      color: #fff;
      cursor: pointer;
      font-size: 13px;
      font-weight: 700;
      line-height: 1;
      padding: 10px 14px;
    }
    .automation-cockpit-review-button:hover {
      background: #1e40af;
      border-color: #1e40af;
    }
    .automation-cockpit-review-button:disabled {
      cursor: default;
      opacity: 0.55;
    }
    .automation-overview-panel-header {
      align-items: flex-start;
      display: flex;
      gap: 16px;
      justify-content: space-between;
      margin-bottom: 18px;
    }
    .automation-overview-title {
      color: var(--workflow-text-strong);
      font-size: 18px;
      font-weight: 700;
      line-height: 1.25;
      margin: 0;
    }
    .automation-overview-subtitle {
      color: var(--workflow-text-muted);
      font-size: 13px;
      line-height: 1.45;
      margin: 6px 0 0;
    }
    .automation-overview-message {
      color: var(--workflow-text-muted);
      font-size: 13px;
      line-height: 1.5;
      margin: 10px 0 0;
      max-width: 680px;
    }
    .automation-overview-grid {
      display: grid;
      background: var(--workflow-surface);
      border: 1px solid var(--workflow-border);
      border-radius: var(--kv-radius-panel);
      gap: 0;
      grid-template-columns: repeat(4, minmax(0, 1fr));
      overflow: hidden;
    }
    .automation-overview-card {
      border-left: 1px solid var(--workflow-border);
      min-width: 0;
      padding: 20px;
    }
    .automation-overview-card:first-child {
      border-left: 0;
    }
    .automation-overview-pill {
      border-radius: 999px;
      display: inline-flex;
      font-size: 11px;
      font-weight: 700;
      line-height: 1;
      margin-bottom: 12px;
      padding: 5px 8px;
      white-space: nowrap;
    }
    .automation-overview-pill.running {
      background: #eff6ff;
      color: #1d4ed8;
    }
    .automation-overview-pill.completed {
      background: #ecfdf5;
      color: #047857;
    }
    .automation-overview-pill.review,
    .automation-overview-pill.waiting {
      background: #fffbeb;
      color: #92400e;
    }
    .automation-overview-value {
      color: var(--workflow-text-strong);
      display: block;
      font-size: 32px;
      font-weight: 650;
      line-height: 1.1;
    }
    .automation-overview-label {
      color: var(--workflow-text-strong);
      display: block;
      font-size: 14px;
      font-weight: 500;
      line-height: 1.35;
      margin-top: 8px;
    }
    .automation-overview-lower {
      display: grid;
      gap: 20px;
      grid-template-columns: repeat(2, minmax(0, 1fr));
      margin: 18px 0;
    }
    .automation-overview-list-card {
      min-width: 0;
      padding: 0 20px;
    }
    .automation-overview-list-header {
      margin-bottom: 16px;
    }
    .automation-overview-section-title {
      color: var(--workflow-text-strong);
      font-size: 16px;
      font-weight: 700;
      margin: 0;
    }
    .automation-overview-section-subtitle {
      color: var(--workflow-text-muted);
      font-size: 13px;
      line-height: 1.4;
      margin: 4px 0 0;
    }
    .automation-overview-list {
      display: flex;
      flex-direction: column;
      gap: 0;
    }
    .automation-overview-list-row {
      align-items: center;
      border-top: 1px solid rgba(148, 163, 184, 0.22);
      display: grid;
      gap: 16px;
      grid-template-columns: minmax(0, 1fr) auto;
      min-width: 0;
      padding: 13px 0;
    }
    .automation-overview-list-row:first-child {
      border-top: 0;
      padding-top: 0;
    }
    .automation-overview-list-row:last-child {
      padding-bottom: 0;
    }
    .automation-overview-list-main {
      min-width: 0;
    }
    .automation-overview-list-name {
      color: var(--workflow-text-strong);
      display: block;
      font-size: 14px;
      font-weight: 650;
      line-height: 1.3;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
    .automation-overview-list-meta {
      color: var(--workflow-text-muted);
      display: block;
      font-size: 12px;
      line-height: 1.35;
      margin-top: 4px;
    }
    .automation-overview-list-side {
      align-items: flex-end;
      display: flex;
      flex-direction: column;
      gap: 8px;
      text-align: right;
    }
    .automation-overview-list-time {
      color: var(--workflow-text-muted);
      font-size: 12px;
      line-height: 1.35;
      white-space: nowrap;
    }
    .automation-overview-card-action {
      --mod-actionbutton-border-color-default: var(--kv-accent-border);
      --mod-actionbutton-border-color-hover: var(--color-accent-border-strong);
      --mod-actionbutton-background-color-default: var(--workflow-accent-bg);
      --mod-actionbutton-background-color-hover: var(--workflow-accent-bg);
      --mod-actionbutton-content-color-default: var(--workflow-accent);
      --mod-actionbutton-content-color-hover: var(--workflow-accent);
      font-size: 12px;
      font-weight: 700;
    }
    .automation-impact-card {
      background: var(--workflow-surface);
      border: 1px solid var(--workflow-border);
      border-radius: var(--kv-radius-panel);
      padding: 20px;
    }
    .automation-impact-grid {
      display: grid;
      gap: 12px;
      grid-template-columns: repeat(4, minmax(0, 1fr));
      margin-top: 16px;
    }
    .automation-impact-item {
      background: var(--workflow-surface-muted);
      border-radius: var(--kv-radius-panel);
      min-width: 0;
      padding: 14px;
    }
    .automation-impact-item.statement {
      background: transparent;
      border: 1px solid var(--workflow-border);
    }
    .automation-impact-value {
      color: var(--workflow-text-strong);
      display: block;
      font-size: 20px;
      font-weight: 700;
      line-height: 1.15;
    }
    .automation-impact-label {
      color: var(--workflow-text-muted);
      display: block;
      font-size: 12px;
      line-height: 1.35;
      margin-top: 6px;
    }
    .automation-impact-value.statement {
      font-size: 15px;
      line-height: 1.25;
    }
    .automation-overview-empty {
      align-items: center;
      background: var(--workflow-surface-muted);
      border-radius: 8px;
      color: var(--workflow-text-muted);
      display: flex;
      font-size: 13px;
      justify-content: space-between;
      padding: 14px;
    }
    .history-filter {
      align-items: center;
      color: var(--color-app-text-secondary);
      display: flex;
      flex: 0 1 auto;
      flex-wrap: wrap;
      font-size: 12px;
      gap: 8px;
      justify-content: flex-end;
      min-width: 0;
    }
    .history-filter strong {
      color: var(--color-app-text);
      font-weight: 700;
    }
    .create-button {
      --mod-actionbutton-border-color-default: var(--kv-accent-border);
      --mod-actionbutton-border-color-hover: var(--color-accent-border-strong);
      --mod-actionbutton-background-color-default: var(--workflow-accent-bg);
      --mod-actionbutton-background-color-hover: var(--workflow-accent-bg);
      --mod-actionbutton-content-color-default: var(--workflow-accent);
      --mod-actionbutton-content-color-hover: var(--workflow-accent);
      font-size: 12px;
      font-weight: 700;
    }
    .workflow-surface {
      background: var(--workflow-surface);
      border: 1px solid var(--workflow-border);
      border-radius: var(--kv-radius-panel);
      box-shadow:
        var(--workflow-shadow),
        0 18px 44px rgba(15, 23, 42, 0.05);
      display: flex;
      flex: 1 1 auto;
      flex-direction: column;
      min-height: 0;
      min-width: 0;
      overflow: hidden;
    }
    .workflow-search {
      flex: 1 1 260px;
      min-width: 0;
      width: auto;
      --mod-search-background-color: var(--workflow-search-bg);
      --mod-search-border-color-default: var(--workflow-search-border);
      --mod-search-border-color-hover: var(--workflow-search-border-hover);
      --mod-search-color-default: var(--workflow-text-strong);
      --mod-search-color-hover: var(--workflow-text-strong);
      --mod-search-color-focus: var(--workflow-text-strong);
      --mod-search-focus-indicator-color: var(--workflow-accent);
    }
    .workflow-sort-menu {
      --mod-actionbutton-border-color-default: transparent;
      --mod-actionbutton-border-color-hover: transparent;
      --mod-actionbutton-background-color-default: transparent;
      --mod-actionbutton-background-color-hover: var(--workflow-row-hover);
      --mod-actionbutton-content-color-default: var(--workflow-text-muted);
      --mod-actionbutton-content-color-hover: var(--workflow-text-strong);
      flex: 0 0 144px;
      font-size: 12px;
      font-weight: 600;
      white-space: nowrap;
    }
    .workflow-sort-menu::part(button) {
      background: transparent;
      border: none;
      color: var(--workflow-text-strong);
      min-width: 144px;
    }
    .workflow-task-list {
      flex: 1;
      min-height: 0;
      min-width: 0;
      overflow: auto;
    }
    .workflow-table-wrap {
      flex: 1;
      min-height: 0;
      min-width: 0;
      overflow: auto;
    }
    .workflow-table {
      --mod-table-border-width: 0px;
      --mod-table-cursor-row-default: default;
      --mod-table-edge-to-content: 20px;
      --mod-table-header-background-color: var(--workflow-surface-muted);
      --mod-table-header-text-color: var(--workflow-text-muted);
      --mod-table-row-background-color: transparent;
      --mod-table-row-background-color-hover: var(--workflow-row-hover);
      --mod-table-row-font-size: 13px;
      min-width: 760px;
      width: 100%;
    }
    .workflow-table-head {
      border-bottom: 1px solid var(--workflow-border);
    }
    .workflow-table-head-cell {
      font-size: 12px;
      font-weight: 700;
    }
    .workflow-table-row {
      transition: background-color 120ms ease;
    }
    .workflow-table-row sp-table-cell {
      align-items: center;
      display: flex;
    }
    .workflow-table-name-cell {
      box-sizing: border-box;
      min-width: 0;
    }
    .workflow-table.with-status .workflow-table-name-cell {
      flex: 0 0 calc(100% - 450px);
      width: calc(100% - 450px);
    }
    .workflow-table.templates .workflow-table-name-cell {
      flex: 0 0 calc(100% - 300px);
      width: calc(100% - 300px);
    }
    .workflow-table-date-cell {
      box-sizing: border-box;
      flex: 0 0 150px;
      width: 150px;
    }
    .workflow-table-status-cell {
      box-sizing: border-box;
      flex: 0 0 150px;
      width: 150px;
    }
    .workflow-table-actions-cell {
      box-sizing: border-box;
      flex: 0 0 150px;
      width: 150px;
    }
    .workflow-table-date-cell,
    .workflow-table-status-cell,
    .workflow-table-actions-cell {
      white-space: nowrap;
    }
    .workflow-table-date-cell,
    .workflow-table-status-cell {
      justify-content: center;
      text-align: center;
    }
    .workflow-table-actions-cell {
      text-align: right;
    }
    .workflow-table .workflow-row-actions {
      justify-content: flex-end;
    }
    .workflow-table-date {
      color: var(--workflow-text-muted);
      font-size: 12px;
      min-width: 0;
      overflow: hidden;
      text-overflow: ellipsis;
    }
    .workflow-attention-layout {
      box-sizing: border-box;
      display: grid;
      flex: 1;
      gap: 0;
      grid-template-columns: minmax(300px, 0.72fr) minmax(460px, 1.28fr);
      min-height: 0;
      min-width: 0;
      padding: 0;
    }
    .workflow-attention-list {
      background: var(--workflow-surface-muted);
      min-height: 0;
      min-width: 0;
      overflow: auto;
      padding: 28px 0 24px;
    }
    .workflow-attention-detail {
      border-left: 1px solid var(--workflow-detail-divider);
      display: flex;
      flex-direction: column;
      min-height: 0;
      min-width: 0;
      overflow: auto;
      padding: 28px 28px 24px 32px;
    }
    .workflow-detail-area-heading {
      color: var(--workflow-text-muted);
      font-size: 11px;
      font-weight: 700;
      letter-spacing: 0.06em;
      line-height: 1.35;
      margin: 0 0 16px;
      text-transform: uppercase;
    }
    .workflow-detail-empty,
    .workflow-detail-panel {
      min-width: 0;
    }
    .workflow-detail-panel {
      display: flex;
      flex-direction: column;
      min-height: 100%;
    }
    .workflow-detail-empty {
      color: var(--workflow-text-muted);
      font-size: 13px;
      padding: 16px 0;
    }
    .workflow-detail-content {
      flex: 1 1 auto;
      min-width: 0;
    }
    .workflow-detail-header {
      align-items: flex-start;
      display: flex;
      gap: 16px;
      justify-content: space-between;
      margin-bottom: 22px;
      min-width: 0;
    }
    .workflow-detail-heading {
      min-width: 0;
    }
    .workflow-detail-title {
      color: var(--workflow-text-strong);
      font-size: 16px;
      font-weight: 600;
      line-height: 1.35;
      margin: 0;
    }
    .workflow-detail-subtitle {
      color: var(--workflow-text-muted);
      font-size: 13px;
      line-height: 1.45;
      margin: 4px 0 0;
    }
    .workflow-detail-section {
      margin-top: 22px;
    }
    .workflow-detail-section-title {
      color: var(--workflow-text-strong);
      font-size: 13px;
      font-weight: 700;
      line-height: 1.35;
      margin: 0 0 8px;
    }
    .workflow-detail-section-text {
      color: var(--workflow-text-strong);
      font-size: 13px;
      line-height: 1.5;
      margin: 0;
    }
    .workflow-detail-section-meta {
      color: var(--workflow-text-muted);
      font-size: 12px;
      line-height: 1.4;
      margin: 4px 0 0;
    }
    .workflow-raw-error-disclosure {
      margin: 0;
    }
    .workflow-raw-error-disclosure summary {
      color: var(--workflow-text-muted);
      cursor: pointer;
      display: inline-flex;
      font-size: 12px;
      font-weight: 600;
      line-height: 1.4;
      list-style-position: inside;
      padding: 2px 0;
    }
    .workflow-raw-error-disclosure summary:hover {
      color: var(--workflow-text-strong);
    }
    .workflow-raw-error {
      background: var(--workflow-raw-error-bg);
      border-radius: 6px;
      color: var(--workflow-text-muted);
      font: 12px/1.5 var(--mono, ui-monospace, SFMono-Regular, Menlo, monospace);
      margin: 8px 0 0;
      max-height: 220px;
      overflow: auto;
      padding: 10px;
      user-select: text;
      -webkit-user-select: text;
      white-space: pre-wrap;
      word-break: break-word;
    }
    .workflow-detail-actions {
      align-items: center;
      display: flex;
      flex: 0 0 auto;
      flex-wrap: wrap;
      gap: 8px;
      justify-content: flex-end;
    }
    .workflow-detail-actions .user-input-action-approve {
      --mod-actionbutton-background-color-default: #1d4ed8;
      --mod-actionbutton-background-color-hover: #1e40af;
      --mod-actionbutton-background-color-down: #1e3a8a;
      --mod-actionbutton-background-color-focus: #1d4ed8;
      --mod-actionbutton-border-color-default: #1d4ed8;
      --mod-actionbutton-border-color-hover: #1e40af;
      --mod-actionbutton-border-color-focus: #1d4ed8;
      --mod-actionbutton-content-color-default: #ffffff;
      --mod-actionbutton-content-color-hover: #ffffff;
      --mod-actionbutton-content-color-down: #ffffff;
      --mod-actionbutton-content-color-focus: #ffffff;
      --mod-actionbutton-focus-ring-color: #1d4ed8;
      font-weight: 700;
    }
    .workflow-debug-menu {
      --mod-actionbutton-border-color-default: transparent;
      --mod-actionbutton-border-color-hover: transparent;
      --mod-actionbutton-background-color-default: transparent;
      --mod-actionbutton-background-color-hover: var(--workflow-row-hover);
      --mod-actionbutton-content-color-default: var(--workflow-text-muted);
      --mod-actionbutton-content-color-hover: var(--workflow-text-strong);
    }
    .workflow-task-group {
      margin-top: 40px;
    }
    .workflow-task-group:first-child {
      margin-top: 0;
    }
    .workflow-task-group-header {
      margin: 0 28px 14px;
    }
    .workflow-task-group-title {
      color: var(--workflow-text-strong);
      font-size: 11px;
      font-weight: 700;
      letter-spacing: 0.06em;
      line-height: 1.35;
      margin: 0;
      text-transform: uppercase;
    }
    .workflow-task-group-description {
      color: var(--workflow-text-muted);
      font-size: 12px;
      line-height: 1.4;
      margin: 4px 0 0;
    }
    .workflow-task-item {
      border-radius: 8px;
      display: grid;
      gap: 16px;
      grid-template-columns: minmax(0, 1fr) minmax(260px, auto);
      outline: 1px solid transparent;
      padding: 16px 20px;
      transition:
        background-color 120ms ease,
        outline-color 120ms ease;
    }
    .workflow-task-item.attention {
      align-items: start;
      border-radius: 0;
      cursor: pointer;
      gap: 12px;
      margin: 0;
      outline: 0;
      padding: 14px 28px;
      grid-template-columns: auto minmax(0, 1fr) auto;
    }
    .workflow-task-item:hover {
      background: var(--workflow-row-hover);
    }
    .workflow-task-item.attention:hover {
      background: var(--workflow-row-hover);
    }
    .workflow-task-item.attention.selected {
      background: var(--workflow-row-selected);
    }
    .workflow-task-main {
      cursor: pointer;
      min-width: 0;
    }
    .workflow-task-time {
      color: var(--workflow-text-muted);
      font-size: 12px;
      line-height: 1.4;
      white-space: nowrap;
    }
    .workflow-task-item.attention:focus-visible,
    .workflow-name:focus-visible {
      outline: 2px solid var(--workflow-accent);
      outline-offset: 3px;
    }
    .workflow-status-icon {
      border-radius: 999px;
      height: 8px;
      margin-top: 6px;
      width: 8px;
    }
    .workflow-status-icon.failed {
      background: var(--color-error-text, #b91c1c);
    }
    .workflow-status-icon.input {
      background: #f59e0b;
    }
    .workflow-row-actions,
    .actions {
      display: flex;
      align-items: center;
      flex-wrap: nowrap;
      gap: 4px;
    }
    .workflow-row-actions {
      opacity: 0;
      pointer-events: none;
      transition: opacity 120ms ease;
    }
    .workflow-task-item:hover .workflow-row-actions,
    .workflow-task-item:focus-within .workflow-row-actions,
    .workflow-table-row:hover .workflow-row-actions,
    .workflow-table-row:focus-within .workflow-row-actions,
    .workflow-row-actions:focus-within {
      opacity: 1;
      pointer-events: auto;
    }
    .workflow-row-actions sp-action-button,
    .workflow-row-actions sp-action-menu,
    .workflow-row-actions sp-action-menu {
      --mod-actionbutton-content-color-default: var(--color-app-text-secondary);
      --mod-actionbutton-content-color-hover: var(--workflow-accent);
      flex: 0 0 auto;
    }
    .workflow-run-button,
    .workflow-dismiss-button,
    .workflow-detail-actions .user-input-action-reject {
      --mod-actionbutton-border-color-default: var(--kv-accent-border);
      --mod-actionbutton-border-color-hover: var(--color-accent-border-strong);
      --mod-actionbutton-background-color-default: var(--workflow-accent-bg);
      --mod-actionbutton-background-color-hover: color-mix(
        in srgb,
        var(--workflow-accent-bg) 86%,
        var(--workflow-accent)
      );
      --mod-actionbutton-content-color-default: var(--workflow-accent);
      --mod-actionbutton-content-color-hover: var(--workflow-accent);
      font-weight: 700;
    }
    .workflow-row-actions {
      justify-content: flex-end;
      width: 100%;
    }
    .workflow-name,
    .workflow-description,
    .cell-main,
    .cell-muted,
    .workflow-title-cell,
    .trigger-summary {
      min-width: 0;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
    .workflow-title-cell,
    .trigger-summary {
      display: block;
      flex: 1 1 auto;
      max-width: 100%;
      width: 100%;
    }
    .trigger-summary {
      margin-top: 4px;
    }
    .trigger-summary .cell-main {
      font-weight: 650;
    }
    .trigger-summary .cell-muted {
      margin-top: 1px;
    }
    .workflow-task-main .trigger-summary {
      margin-top: 8px;
    }
    .workflow-task-main .trigger-summary .cell-main,
    .workflow-task-main .trigger-summary .cell-muted {
      white-space: normal;
    }
    .workflow-name {
      color: var(--workflow-text-strong);
      display: block;
      font-size: 14px;
      font-weight: 600;
    }
    .workflow-description {
      color: var(--color-app-text-secondary, var(--workflow-text-muted));
      display: block;
      font-size: 12px;
      margin-top: 2px;
    }
    .workflow-problem-summary {
      color: var(--workflow-text-strong);
      display: block;
      font-size: 13px;
      font-weight: 500;
      line-height: 1.4;
      margin-top: 4px;
      white-space: normal;
    }
    .workflow-task-meta-line {
      align-items: center;
      color: var(--workflow-text-muted);
      display: flex;
      flex-wrap: wrap;
      font-size: 12px;
      gap: 6px;
      line-height: 1.4;
      margin-top: 6px;
    }
    .workflow-task-error-summary {
      color: var(--workflow-text-strong);
      display: block;
      font-size: 13px;
      font-weight: 500;
      line-height: 1.4;
      margin-top: 4px;
    }
    .workflow-task-meta-dot {
      color: var(--workflow-text-muted);
    }
    .status-failed {
      color: var(--color-error-text, #dc2626);
      font-weight: 600;
    }
    .cell-main {
      color: var(--color-app-text, #1f1f1f);
      display: block;
      flex: 1 1 auto;
      font-weight: 650;
      max-width: 100%;
    }
    .cell-muted {
      color: var(--color-app-text-secondary, var(--workflow-text-muted));
      display: block;
      font-size: 12px;
      margin-top: 2px;
    }
    .status-chip,
    .template-chip {
      align-items: center;
      border-radius: 999px;
      display: inline-flex;
      font-size: 12px;
      font-weight: 700;
      line-height: 1.2;
      padding: 4px 9px;
      white-space: nowrap;
    }
    .status-chip.running {
      background: var(--workflow-accent-bg);
      color: var(--workflow-accent);
    }
    .status-chip.success {
      background: var(--color-success-bg, #efe);
      color: var(--color-success-text, #15803d);
    }
    .status-chip.failed {
      background: var(--color-error-bg, #fee);
      color: var(--color-error-text, #b91c1c);
    }
    .status-chip.neutral,
    .template-chip {
      background: var(--workflow-surface-muted);
      color: var(--color-app-text-secondary);
    }
    .meta-inline {
      align-items: center;
      display: flex;
      flex-wrap: wrap;
      gap: 6px;
      margin-top: 6px;
    }
    .muted {
      color: var(--color-app-text-secondary);
    }
    .state-row {
      align-items: center;
      color: var(--color-app-text-secondary);
      display: flex;
      justify-content: center;
      min-height: 180px;
      text-align: center;
      width: 100%;
    }
    .empty-state {
      align-items: center;
      box-sizing: border-box;
      color: var(--color-app-text-secondary);
      display: flex;
      flex-direction: column;
      gap: 10px;
      justify-content: center;
      min-height: 240px;
      padding: 32px 24px;
      text-align: center;
      width: 100%;
    }
    .empty-state-title {
      color: var(--workflow-text-strong);
      font-size: 15px;
      font-weight: 700;
      line-height: 1.3;
      margin: 0;
    }
    .empty-state-description {
      color: var(--color-app-text-secondary);
      font-size: 13px;
      line-height: 1.45;
      margin: 0;
      max-width: 360px;
    }
    .empty-state-actions {
      align-items: center;
      display: flex;
      gap: 8px;
      justify-content: center;
      margin-top: 4px;
    }
    .user-input-wrap {
      box-sizing: border-box;
      display: flex;
      flex-direction: column;
      gap: var(--gap-2);
      width: 100%;
    }
    .user-input-header {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 8px;
      flex-wrap: wrap;
    }
    .user-input-title {
      color: var(--workflow-text-strong);
      font-size: 13px;
      font-weight: 700;
      line-height: 1.35;
      margin: 0;
    }
    .user-input-message {
      color: var(--workflow-text-strong);
      font-size: 13px;
      line-height: 1.5;
      margin: 0;
    }
    .input-preview {
      background: var(--workflow-surface);
      border: 1px solid var(--workflow-border);
      border-radius: 8px;
      color: var(--color-app-text);
      max-height: 220px;
      overflow: auto;
      padding: var(--gap-2);
      font-family: var(
        --mono,
        ui-monospace,
        SFMono-Regular,
        Menlo,
        monospace
      );
      font-size: 12px;
      white-space: pre-wrap;
      word-break: break-word;
      margin: 0;
    }
    .error-panel {
      background: var(--color-error-bg, #fee);
      border: 1px solid rgba(220, 38, 38, 0.18);
      border-radius: 8px;
      color: var(--color-error-text, #b91c1c);
      font-size: 13px;
      line-height: 1.35;
      margin-bottom: 16px;
      padding: 12px 14px;
    }

    @media (max-width: 768px) {
      .overview {
        padding: 16px;
      }
      .overview-header {
        align-items: stretch;
        flex-direction: column;
      }
      .overview-sections {
        align-items: stretch;
        flex-wrap: wrap;
        padding-inline: 0;
      }
      .overview-section-search {
        flex-basis: 100%;
        max-width: none;
        margin-left: 0;
        order: 3;
        padding: 0 12px 10px;
      }
      .automation-overview-grid {
        grid-template-columns: repeat(2, minmax(0, 1fr));
      }
      .automation-overview-lower {
        grid-template-columns: 1fr;
      }
      .automation-impact-grid {
        grid-template-columns: repeat(2, minmax(0, 1fr));
      }
      .automation-overview-panel-header {
        flex-direction: column;
      }
      .automation-cockpit-card {
        background: url('/img/automation-cockpit-light.webp') center / cover;
        grid-template-columns: 1fr;
        margin: 0 -20px;
        min-height: 220px;
        padding: 22px;
      }
      :host-context(sp-theme[color='dark']) .automation-cockpit-card {
        background: url('/img/automation-cockpit-dark.webp') center / cover;
      }
      .automation-cockpit-actions {
        align-items: flex-start;
      }
      .automation-cockpit-support {
        justify-self: stretch;
      }
      .overview-title {
        padding-left: var(--kv-mobile-title-inline-start);
      }
      .workflow-search {
        width: 100%;
      }
      .workflow-task-item {
        display: flex;
        flex-direction: column;
        gap: 12px;
        padding: 16px 20px;
      }
      .workflow-task-item.attention {
        display: grid;
        grid-template-columns: auto minmax(0, 1fr) auto;
        margin: 0;
        padding: 12px 14px;
      }
      .workflow-attention-layout {
        display: flex;
        flex-direction: column;
        padding: 20px;
      }
      .workflow-attention-list {
        padding: 0;
      }
      .workflow-attention-detail {
        border-left: 0;
        border-top: 1px solid var(--workflow-detail-divider);
        padding: 24px 0 0;
      }
      .workflow-detail-header {
        flex-direction: column;
      }
      .workflow-detail-actions {
        justify-content: flex-start;
      }
      .workflow-row-actions {
        flex: 0 0 auto;
      }
    }

    @media (max-width: 520px) {
      .automation-overview-grid,
      .automation-impact-grid {
        grid-template-columns: 1fr;
      }
      .automation-overview-card {
        border-left: 0;
        border-top: 1px solid var(--workflow-border);
      }
      .automation-overview-card:first-child {
        border-top: 0;
      }
    }
    `,
  ];

  @property({ attribute: false }) accessor workflows: WorkflowSummary[] = [];
  @property({ type: Boolean }) accessor loading = false;
  @property({ attribute: false }) accessor errorMessage: string | null = null;
  @property({ type: Boolean }) accessor createLoading = false;
  @property({ attribute: false }) accessor runIds = new Set<number>();
  @property({ attribute: false }) accessor deleteIds = new Set<number>();
  @property({ attribute: false }) accessor userInputRequests = new Map<
    number,
    WorkflowUserInputRequest
  >();
  @property({ type: Number, attribute: false }) accessor userInputActionTaskId:
    | number
    | undefined = undefined;
  @property({ attribute: false }) accessor userInputActionType:
    | string
    | undefined = undefined;
  @state() accessor pendingDelete: WorkflowSummary | null = null;
  @state() accessor pendingReset: PendingWorkflowReset | null = null;
  @state() accessor sortKey: WorkflowSortKey = 'name';
  @state() accessor sortDirection: SortDirection = 'asc';
  @state() accessor searchTerm = '';
  @state() accessor historySearchTerm = '';
  @state() accessor activeSection: OverviewSection = 'overview';
  @state() accessor selectedAttentionWorkflowId: Id | null = null;
  @state() accessor recentCompletedAudits: WorkflowAuditSummary[] = [];
  @state() accessor overviewImpact = {
    completedRuns: 0,
    manualStarts: 0,
  };
  @state() accessor auditFilter: {
    workflowId: number | null;
    workflowName: string | null;
    auditId?: number | null;
    errorsOnly?: boolean;
  } | null = null;
  @state() accessor historyCount: number | null = null;
  @query('kv-module-audit') accessor auditModule: {
    clearFilter: () => void;
    setWorkflowFilter: (
      workflowId: number | null,
      workflowName?: string | null,
      options?: { auditId?: number | null; errorsOnly?: boolean }
    ) => void;
  } | null = null;
  private overviewAuditRefreshTimeout?: ReturnType<typeof setTimeout>;

  connectedCallback(): void {
    super.connectedCallback();
    const persisted = loadPersistedWorkflowOverviewSort();
    this.sortKey = persisted.sortKey;
    this.sortDirection = persisted.sortDirection;
    this.activeSection = loadPersistedWorkflowOverviewSection();
    void this.loadRecentCompletedAudits();
    void this.loadOverviewImpact();
  }

  disconnectedCallback(): void {
    if (this.overviewAuditRefreshTimeout) {
      clearTimeout(this.overviewAuditRefreshTimeout);
      this.overviewAuditRefreshTimeout = undefined;
    }
    super.disconnectedCallback();
  }

  private queueOverviewAuditRefresh() {
    if (this.overviewAuditRefreshTimeout) {
      clearTimeout(this.overviewAuditRefreshTimeout);
    }
    this.overviewAuditRefreshTimeout = setTimeout(() => {
      this.overviewAuditRefreshTimeout = undefined;
      void this.loadRecentCompletedAudits();
      void this.loadOverviewImpact();
    }, 250);
  }

  private get currentMonthStartIso() {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth(), 1).toISOString();
  }

  private async loadRecentCompletedAudits() {
    try {
      const response = await auditService.list(undefined, {
        status: 'success',
        page: 1,
        limit: 3,
      });
      this.recentCompletedAudits = response.audits.filter(
        (audit) => audit.finishedAt
      );
    } catch {
      this.recentCompletedAudits = [];
    }
  }

  private async loadOverviewImpact() {
    const startedFrom = this.currentMonthStartIso;
    try {
      const [completed, manual] = await Promise.all([
        auditService.list(undefined, {
          status: 'success',
          startedFrom,
          page: 1,
          limit: 1,
        }),
        auditService.list(undefined, {
          trigger: 'manual',
          startedFrom,
          page: 1,
          limit: 1,
        }),
      ]);
      this.overviewImpact = {
        completedRuns: completed.pagination.total,
        manualStarts: manual.pagination.total,
      };
    } catch {
      this.overviewImpact = {
        completedRuns: 0,
        manualStarts: 0,
      };
    }
  }

  private formatWorkflowName(workflow: WorkflowSummary) {
    if (workflow.name && workflow.name.trim().length > 0) {
      return workflow.name.trim();
    }
    return `Automation ${workflow.id}`;
  }

  private formatWorkflowDescription(workflow: WorkflowSummary) {
    const description = workflow.description?.trim();
    if (description) {
      return description;
    }
    return '';
  }

  private formatTrigger(workflow: WorkflowSummary) {
    if (!workflow.cronString) return '';
    const timezone = workflow.cronTimezone ?? 'UTC';
    return `${workflow.cronString} (${timezone})`;
  }

  private formatScheduledRunDate(value?: string | null) {
    return formatDateTime(value, {
      dateStyle: 'medium',
      timeStyle: 'short',
    });
  }

  private formatNaturalDate(value?: string | Date | null) {
    return formatNaturalDateValue(value, { locale: getLocale() });
  }

  private getStatusClass(status?: WorkflowAuditStatus | null) {
    switch (status) {
      case 'running':
        return 'running';
      case 'success':
        return 'success';
      case 'failed':
        return 'failed';
      default:
        return 'neutral';
    }
  }

  private formatStatus(status?: WorkflowAuditStatus | null) {
    switch (status) {
      case 'running':
        return msg('Running');
      case 'success':
        return msg('Success');
      case 'failed':
        return msg('Failed');
      default:
        return msg('Not run');
    }
  }

  private renderStatusChip(status?: WorkflowAuditStatus | null) {
    return html`
      <span class="status-chip ${this.getStatusClass(status)}">
        ${this.formatStatus(status)}
      </span>
    `;
  }

  private renderWorkflowStatusChip(workflow: WorkflowSummary) {
    if (this.workflowRequiresUserInput(workflow)) {
      return html`<span class="status-chip neutral">${msg('Awaiting input')}</span>`;
    }
    return this.renderStatusChip(workflow.lastAuditStatus);
  }

  private formatWorkflowStatus(workflow: WorkflowSummary) {
    if (this.workflowRequiresUserInput(workflow)) return msg('Awaiting input');
    return this.formatStatus(workflow.lastAuditStatus);
  }

  private renderTableDate(workflow: WorkflowSummary) {
    const dateValue =
      this.activeSection === 'templates'
        ? workflow.updatedAt
        : workflow.lastExecutedAt;
    if (!dateValue) return nothing;
    const date = this.formatNaturalDate(dateValue);
    return html`<span class="workflow-table-date" title=${date.title}>
      ${date.label}
    </span>`;
  }

  private formatFailedTaskErrorDetails(workflow: WorkflowSummary) {
    const failedTaskError = workflow.failedTaskErrors?.[0];
    const error = failedTaskError?.error.trim();
    if (!failedTaskError || !error) return '';
    const lastRun = workflow.lastExecutedAt
      ? new Date(workflow.lastExecutedAt).toISOString()
      : 'Unknown';
    return [
      `Automation: ${this.formatWorkflowName(workflow)} (#${workflow.id})`,
      `Run: #${failedTaskError.auditId}`,
      `Last result: ${workflow.lastAuditStatus ?? 'Unknown'}`,
      `Last run: ${lastRun}`,
      `Failed step: ${failedTaskError.taskName} (#${failedTaskError.taskId})`,
      '',
      'Error message:',
      error,
    ].join('\n');
  }

  private async copyFailedTaskError(event: Event, workflow: WorkflowSummary) {
    event.stopPropagation();
    const details = this.formatFailedTaskErrorDetails(workflow);
    if (!details) return;
    if (!navigator?.clipboard?.writeText) {
      emitToast(this, {
        variant: 'error',
        message: msg(
          'Clipboard is unavailable. Open history to view the error details.'
        ),
      });
      return;
    }
    try {
      await navigator.clipboard.writeText(details);
      emitToast(this, {
        variant: 'success',
        message: msg('Error details copied'),
      });
    } catch {
      emitToast(this, {
        variant: 'error',
        message: msg(
          'Failed to copy error details. Open history to view them.'
        ),
      });
    }
  }

  private openFailedTaskDetails(event: Event, workflow: WorkflowSummary) {
    event.stopPropagation();
    const auditId = workflow.failedTaskErrors?.[0]?.auditId;
    this.openAudit(workflow, {
      auditId: typeof auditId === 'number' ? auditId : null,
      errorsOnly: true,
    });
  }

  private handleFailedSupportAction = (
    event: Event,
    workflow: WorkflowSummary
  ) => {
    event.stopPropagation();
    const value = (event.currentTarget as { value?: string } | null)?.value;
    switch (value) {
      case 'rerun':
        this.runWorkflow(workflow.id);
        break;
      case 'open-workflow':
        this.openWorkflow(workflow.id);
        break;
      case 'copy-error':
        void this.copyFailedTaskError(event, workflow);
        break;
      case 'view-error-details':
        this.openFailedTaskDetails(event, workflow);
        break;
      default:
        break;
    }
  };

  private getFailedTaskError(workflow: WorkflowSummary) {
    const failedTaskError = workflow.failedTaskErrors?.[0];
    const error = failedTaskError?.error.trim();
    if (!failedTaskError || !error) return null;
    return { ...failedTaskError, error };
  }

  private getAdditionalFailedTaskErrorCount(workflow: WorkflowSummary) {
    const total =
      workflow.failedTaskErrorCount ?? workflow.failedTaskErrors?.length ?? 0;
    return Math.max(0, total - 1);
  }

  private workflowNeedsAttention(workflow: WorkflowSummary) {
    return (
      workflow.lastAuditStatus === 'failed' ||
      workflow.pendingInteraction != null ||
      this.userInputRequests.has(Number(workflow.id))
    );
  }

  private workflowRequiresUserInput(workflow: WorkflowSummary) {
    return (
      workflow.pendingInteraction != null ||
      this.userInputRequests.has(Number(workflow.id))
    );
  }

  private get needsAttentionCount() {
    return (this.workflows ?? []).filter((workflow) =>
      this.workflowNeedsAttention(workflow)
    ).length;
  }

  private get availableCount() {
    return (this.workflows ?? []).filter((workflow) => !workflow.isTemplate)
      .length;
  }

  private get templateCount() {
    return (this.workflows ?? []).filter((workflow) => workflow.isTemplate)
      .length;
  }

  private get overviewKpis() {
    const workflows = this.workflows ?? [];
    const thirtyDaysAgo = Date.now() - 30 * 24 * 60 * 60 * 1000;
    const recentlyExecuted = workflows.filter((workflow) => {
      if (!workflow.lastExecutedAt) return false;
      const timestamp = new Date(workflow.lastExecutedAt).getTime();
      return !Number.isNaN(timestamp) && timestamp >= thirtyDaysAgo;
    });
    return {
      completed: recentlyExecuted.filter(
        (workflow) => workflow.lastAuditStatus === 'success'
      ).length,
      failed: recentlyExecuted.filter(
        (workflow) => workflow.lastAuditStatus === 'failed'
      ).length,
      awaitingInput: workflows.filter((workflow) =>
        this.workflowRequiresUserInput(workflow)
      ).length,
    };
  }

  private get latestWorkflowExecutionAt() {
    return (this.workflows ?? []).reduce<string | null>((latest, workflow) => {
      if (!workflow.lastExecutedAt) return latest;
      const timestamp = new Date(workflow.lastExecutedAt).getTime();
      if (Number.isNaN(timestamp)) return latest;
      if (!latest) return workflow.lastExecutedAt;
      const latestTimestamp = new Date(latest).getTime();
      return timestamp > latestTimestamp ? workflow.lastExecutedAt : latest;
    }, null);
  }

  private workflowInActiveSection(workflow: WorkflowSummary) {
    switch (this.activeSection) {
      case 'overview':
        return false;
      case 'needsAttention':
        return this.workflowNeedsAttention(workflow);
      case 'available':
        return !workflow.isTemplate;
      case 'templates':
        return Boolean(workflow.isTemplate);
      case 'history':
        return false;
    }
  }

  private renderTriggerSummary(workflow: WorkflowSummary) {
    const triggerText = this.formatTrigger(workflow);
    if (!triggerText) return nothing;
    const scheduledRun = this.formatScheduledRunDate(
      workflow.cronNextExecutionAt
    );
    return html`
      <div class="trigger-summary">
        <span class="cell-main">${msg('Scheduled')}: ${scheduledRun}</span>
      </div>
    `;
  }

  private workflowMatchesSearch(workflow: WorkflowSummary, query: string) {
    if (!query) {
      return true;
    }
    const haystack = [
      this.formatWorkflowName(workflow),
      this.formatWorkflowDescription(workflow),
      String(workflow.id),
      this.formatTrigger(workflow),
      this.formatWorkflowStatus(workflow),
      this.formatNaturalDate(workflow.lastExecutedAt ?? null).label,
    ]
      .join(' ')
      .toLowerCase();
    return haystack.includes(query);
  }

  private handleSearchInput = (event: Event) => {
    const target = event.currentTarget as { value?: string };
    this.searchTerm = target.value ?? '';
  };

  private handleHistorySearchInput = (event: Event) => {
    const target = event.currentTarget as { value?: string };
    this.historySearchTerm = target.value ?? '';
  };

  private createWorkflow = () => {
    this.dispatchEvent(
      new CustomEvent('create-workflow', { bubbles: true, composed: true })
    );
  };

  private runWorkflow = (id: Id) => {
    this.dispatchEvent(
      new CustomEvent('run-workflow', {
        detail: { id },
        bubbles: true,
        composed: true,
      })
    );
  };

  private openWorkflow = (id: Id) => {
    this.dispatchEvent(
      new CustomEvent('open-workflow', {
        detail: { id },
        bubbles: true,
        composed: true,
      })
    );
  };

  private openAudit = (
    workflow: WorkflowSummary,
    options: { auditId?: number | null; errorsOnly?: boolean } = {}
  ) => {
    const workflowId = Number(workflow.id);
    if (!Number.isFinite(workflowId)) return;

    const workflowName = this.formatWorkflowName(workflow);
    this.auditFilter = { workflowId, workflowName, ...options };
    this.activeSection = 'history';
    this.persistWorkflowOverviewSection();

    try {
      localStorage.setItem(
        'kv.audit.filter',
        JSON.stringify({ workflowId, workflowName, ...options })
      );
    } catch {
      /* no-op */
    }

    void this.updateComplete.then(() => {
      this.auditModule?.setWorkflowFilter(workflowId, workflowName, options);
    });
  };

  private requestDeleteWorkflow = (workflow: WorkflowSummary) => {
    this.pendingDelete = workflow;
  };

  private cancelDelete = () => {
    this.pendingDelete = null;
  };

  private confirmDelete = () => {
    const workflow = this.pendingDelete;
    if (!workflow) return;
    this.pendingDelete = null;
    this.dispatchEvent(
      new CustomEvent('delete-workflow', {
        detail: { id: workflow.id },
        bubbles: true,
        composed: true,
      })
    );
  };

  private dispatchResetWorkflow(workflow: WorkflowSummary) {
    this.dispatchEvent(
      new CustomEvent('reset-workflow', {
        detail: { id: workflow.id },
        bubbles: true,
        composed: true,
      })
    );
  }

  private requestResetWorkflow = (
    workflow: WorkflowSummary,
    action: WorkflowResetAction
  ) => {
    if (action === 'stop') {
      this.pendingReset = { workflow };
      return;
    }
    this.dispatchResetWorkflow(workflow);
  };

  private cancelReset = () => {
    this.pendingReset = null;
  };

  private confirmReset = () => {
    const pending = this.pendingReset;
    if (!pending) return;
    this.pendingReset = null;
    this.dispatchResetWorkflow(pending.workflow);
  };

  private selectAttentionWorkflow = (workflow: WorkflowSummary) => {
    this.selectedAttentionWorkflowId = workflow.id;
  };

  private emitOverviewInteraction = (
    detail: OverviewInteractionActionDetail
  ) => {
    this.dispatchEvent(
      new CustomEvent<OverviewInteractionActionDetail>(
        'overview-interaction-action',
        {
          detail,
          bubbles: true,
          composed: true,
        }
      )
    );
  };

  private getTimestamp(value?: string | null): number | null {
    if (!value) return null;
    const ts = new Date(value).getTime();
    return Number.isFinite(ts) ? ts : null;
  }

  private getWorkflowNameForSort(workflow: WorkflowSummary): string {
    return this.formatWorkflowName(workflow).toLocaleLowerCase();
  }

  private compareWorkflows(
    left: WorkflowSummary,
    right: WorkflowSummary,
    sortKey: WorkflowSortKey,
    sortDirection: SortDirection
  ): number {
    const factor = sortDirection === 'asc' ? 1 : -1;
    if (sortKey === 'name') {
      return (
        this.getWorkflowNameForSort(left).localeCompare(
          this.getWorkflowNameForSort(right)
        ) * factor
      );
    }
    if (sortKey === 'lastExecutedAt') {
      const leftTs = this.getTimestamp(
        this.activeSection === 'templates'
          ? left.updatedAt
          : left.lastExecutedAt
      );
      const rightTs = this.getTimestamp(
        this.activeSection === 'templates'
          ? right.updatedAt
          : right.lastExecutedAt
      );
      if (leftTs == null && rightTs == null) return 0;
      if (leftTs == null) return 1;
      if (rightTs == null) return -1;
      return (leftTs - rightTs) * factor;
    }
    return 0;
  }

  private get sortedWorkflows(): WorkflowSummary[] {
    const query = this.shouldShowListControls
      ? this.searchTerm.trim().toLowerCase()
      : '';
    return this.sectionWorkflows
      .filter((workflow) => this.workflowMatchesSearch(workflow, query))
      .sort((left, right) =>
        this.compareWorkflows(left, right, this.sortKey, this.sortDirection)
      );
  }

  private get sectionWorkflows(): WorkflowSummary[] {
    if (this.activeSection === 'overview' || this.activeSection === 'history') {
      return [];
    }
    return [...(this.workflows ?? [])].filter((workflow) =>
      this.workflowInActiveSection(workflow)
    );
  }

  private get emptyMessage() {
    if (this.shouldShowListControls && this.searchTerm.trim()) {
      return msg('No automations match this search.');
    }
    if (this.activeSection === 'needsAttention') {
      return msg('No automations need attention.');
    }
    if (this.activeSection === 'templates') {
      return msg('No templates yet.');
    }
    return msg('No automations yet.');
  }

  private renderWorkflowEmptyState() {
    const hasSearch = this.shouldShowListControls && this.searchTerm.trim();
    if (hasSearch) {
      return html`<div class="empty-state">
        <h3 class="empty-state-title">${msg('No matching automations')}</h3>
        <p class="empty-state-description">
          ${msg('Try a different search term to find automations in this tab.')}
        </p>
      </div>`;
    }

    if (this.activeSection === 'available') {
      return html`<div class="empty-state">
        <h3 class="empty-state-title">${msg('No active automations yet')}</h3>
        <p class="empty-state-description">
          ${msg('Create an automation to start running repeatable work from here.')}
        </p>
        <div class="empty-state-actions">
          <sp-action-button
            class="create-button"
            ?disabled=${this.createLoading}
            @click=${this.createWorkflow}
          >
            <sp-icon-add slot="icon"></sp-icon-add>
            ${msg('New automation')}
          </sp-action-button>
        </div>
      </div>`;
    }

    if (this.activeSection === 'templates') {
      return html`<div class="empty-state">
        <h3 class="empty-state-title">${msg('No templates yet')}</h3>
        <p class="empty-state-description">
          ${msg('Create an automation and mark it as a template when it should be reused.')}
        </p>
        <div class="empty-state-actions">
          <sp-action-button
            class="create-button"
            ?disabled=${this.createLoading}
            @click=${this.createWorkflow}
          >
            <sp-icon-add slot="icon"></sp-icon-add>
            ${msg('New automation')}
          </sp-action-button>
        </div>
      </div>`;
    }

    return html`<div class="empty-state">
      <h3 class="empty-state-title">${this.emptyMessage}</h3>
    </div>`;
  }

  private get activeSectionItemCount() {
    if (this.activeSection === 'history') {
      return this.historyCount ?? 10;
    }
    if (this.activeSection === 'overview') {
      return 0;
    }
    return this.sectionWorkflows.length;
  }

  private getOverviewSectionLabel(section: OverviewSection): string {
    switch (section) {
      case 'overview':
        return msg('Overview');
      case 'needsAttention':
        return msg('Needs attention');
      case 'available':
        return msg('Active');
      case 'templates':
        return msg('Templates');
      case 'history':
        return msg('History');
    }
  }

  private get shouldShowListControls() {
    return (
      this.activeSection !== 'overview' && this.activeSectionItemCount >= 10
    );
  }

  private persistWorkflowOverviewSort() {
    try {
      localStorage.setItem(
        WORKFLOW_OVERVIEW_SORT_STORAGE_KEY,
        JSON.stringify({
          sortKey: this.sortKey,
          sortDirection: this.sortDirection,
        })
      );
    } catch {
      /* no-op */
    }
  }

  private persistWorkflowOverviewSection() {
    try {
      localStorage.setItem(
        WORKFLOW_OVERVIEW_SECTION_STORAGE_KEY,
        this.activeSection
      );
    } catch {
      /* no-op */
    }
  }

  private get sortControlValue() {
    return `${this.sortKey}${MOBILE_SORT_VALUE_SEP}${this.sortDirection}`;
  }

  private get sortControlLabel() {
    if (this.sortKey === 'name') {
      return this.sortDirection === 'asc' ? 'Name A-Z' : 'Name Z-A';
    }
    return this.sortDirection === 'asc'
      ? msg('Oldest first')
      : msg('Recent first');
  }

  private formatAutomationCount(count: number) {
    return count === 1 ? msg('1 automation') : msg(str`${count} automations`);
  }

  private setSort(sortKey: WorkflowSortKey, sortDirection: SortDirection) {
    this.sortKey = sortKey;
    this.sortDirection = sortDirection;
    this.persistWorkflowOverviewSort();
  }

  private onSortMenuChange = (event: Event) => {
    const raw = (event.currentTarget as { value?: string } | null)?.value;
    if (typeof raw !== 'string') return;
    const [k, d] = raw.split(MOBILE_SORT_VALUE_SEP);
    if (isWorkflowSortKey(k) && isSortDirection(d)) {
      this.setSort(k, d);
    }
  };

  private selectSection = (section: OverviewSection) => {
    if (section === 'needsAttention' && this.needsAttentionCount === 0) {
      return;
    }
    this.activeSection = section;
    this.persistWorkflowOverviewSection();
  };

  private get auditFilterLabel() {
    const filter = this.auditFilter;
    if (!filter || filter.workflowId == null) return null;
    return filter.workflowName ?? `Automation ${filter.workflowId}`;
  }

  private onAuditFilterStateChange = (
    event: CustomEvent<{
      workflowId: number | null;
      workflowName: string | null;
      auditId?: number | null;
      errorsOnly?: boolean;
    }>
  ) => {
    this.auditFilter = event.detail;
  };

  private onAuditSummaryStateChange = (
    event: CustomEvent<{ count?: number }>
  ) => {
    this.historyCount =
      typeof event.detail.count === 'number' ? event.detail.count : null;
  };

  private clearAuditFilter = () => {
    this.auditModule?.clearFilter();
  };

  private renderHistoryFilter() {
    const label = this.auditFilterLabel;
    if (!label) return nothing;
    return html`<div class="history-filter">
      <span>${msg('Filtered by')} <strong>${label}</strong></span>
      <sp-button size="s" variant="secondary" @click=${this.clearAuditFilter}>
        ${msg('Clear')}
      </sp-button>
    </div>`;
  }

  protected updated(changedProperties: PropertyValues<this>): void {
    if (changedProperties.has('workflows')) {
      this.queueOverviewAuditRefresh();
    }
    const attentionInputsChanged =
      changedProperties.has('workflows') ||
      changedProperties.has('userInputRequests') ||
      changedProperties.has('loading');
    if (
      attentionInputsChanged &&
      !this.loading &&
      this.activeSection === 'needsAttention' &&
      this.needsAttentionCount === 0
    ) {
      this.activeSection = 'overview';
      this.selectedAttentionWorkflowId = null;
      this.persistWorkflowOverviewSection();
      return;
    }
    if (this.activeSection === 'needsAttention') {
      const workflows = this.sortedWorkflows;
      if (
        workflows.length > 0 &&
        !workflows.some(
          (workflow) => workflow.id === this.selectedAttentionWorkflowId
        )
      ) {
        this.selectedAttentionWorkflowId = workflows[0].id;
      }
    }
  }

  private renderSectionTabs() {
    return html`
      <div class="overview-sections">
        <div class="overview-tab-list" role="tablist" aria-label=${msg('Automation sections')}>
          ${OVERVIEW_SECTIONS.map((section) => {
            const count = this.getSectionCount(section.key);
            const disabled =
              section.key === 'needsAttention' &&
              this.needsAttentionCount === 0;
            const selected = this.activeSection === section.key;
            return html`
              <button
                class="overview-section-tab"
                type="button"
                role="tab"
                aria-selected=${selected ? 'true' : 'false'}
                ?disabled=${disabled}
                @click=${() => this.selectSection(section.key)}
              >
                <span class="overview-section-tab-content">
                  <span>${this.getOverviewSectionLabel(section.key)}</span>
                  ${
                    count == null
                      ? nothing
                      : html`<span class="overview-tab-count">${count}</span>`
                  }
                </span>
              </button>
            `;
          })}
        </div>
        ${this.renderSectionControls()}
      </div>
    `;
  }

  private renderSectionControls() {
    if (this.activeSection === 'overview') return nothing;
    const hasHistoryFilter = this.auditFilterLabel != null;
    const historyFilter = hasHistoryFilter
      ? this.renderHistoryFilter()
      : nothing;
    if (!this.shouldShowListControls) {
      return this.activeSection === 'history' && hasHistoryFilter
        ? html`<div class="overview-section-filter">${historyFilter}</div>`
        : nothing;
    }
    return html`
      ${
        this.activeSection === 'history'
          ? hasHistoryFilter
            ? html`<div class="overview-section-filter">${historyFilter}</div>`
            : nothing
          : html`<div class="overview-section-sort">
              ${this.renderSortControl()}
            </div>`
      }
      <div class="overview-section-search">
        ${this.renderSearchControl()}
      </div>
    `;
  }

  private renderSearchControl() {
    const isHistory = this.activeSection === 'history';
    const searchLabel = isHistory
      ? msg('Search history')
      : msg('Search automations');
    return html`
      <sp-search
        class="workflow-search"
        size="m"
        aria-label=${searchLabel}
        label=${searchLabel}
        placeholder=${searchLabel}
        .value=${isHistory ? this.historySearchTerm : this.searchTerm}
        @input=${
          isHistory ? this.handleHistorySearchInput : this.handleSearchInput
        }
      ></sp-search>
    `;
  }

  private getSectionCount(section: OverviewSection) {
    switch (section) {
      case 'overview':
        return null;
      case 'needsAttention':
        return this.needsAttentionCount;
      case 'available':
        return this.availableCount;
      case 'templates':
        return this.templateCount;
      case 'history':
        return null;
    }
  }

  private handleWorkflowOverflowAction = (
    event: Event,
    workflow: WorkflowSummary
  ) => {
    const value = (event.currentTarget as { value?: string } | null)?.value;
    switch (value) {
      case 'edit':
        this.openWorkflow(workflow.id);
        break;
      case 'history':
        this.openAudit(workflow);
        break;
      case 'delete':
        this.requestDeleteWorkflow(workflow);
        break;
      default:
        break;
    }
  };

  private renderWorkflowActionButtons(
    workflow: WorkflowSummary,
    containerClass: 'workflow-row-actions'
  ) {
    const isRunning = this.runIds.has(workflow.id);
    const isDeleting = this.deleteIds.has(workflow.id);
    const hasUserInputRequest = this.userInputRequests.has(Number(workflow.id));
    const showStop = isRunning || hasUserInputRequest;
    const retry = workflow.lastAuditStatus === 'failed';
    return html`
      <div class=${containerClass}>
        ${
          workflow.isTemplate
            ? html`<sp-action-button
                class="workflow-run-button"
                label=${msg('Edit automation')}
                @click=${() => this.openWorkflow(workflow.id)}
              >
                <sp-icon-edit slot="icon"></sp-icon-edit>
                ${msg('Edit')}
              </sp-action-button>`
            : showStop
              ? html`<sp-action-button
                class="workflow-run-button"
                variant="negative"
                label=${msg('Stop automation')}
                @click=${() => this.requestResetWorkflow(workflow, 'stop')}
              >
                <sp-icon-cancel slot="icon"></sp-icon-cancel>
                ${msg('Stop')}
              </sp-action-button>`
              : html`<sp-action-button
                class="workflow-run-button"
                label=${retry ? msg('Retry automation') : msg('Run automation')}
                @click=${() => this.runWorkflow(workflow.id)}
              >
                <sp-icon-play slot="icon"></sp-icon-play>
                ${retry ? msg('Retry') : msg('Run')}
              </sp-action-button>`
        }
        <sp-action-menu
          quiet
          label=${msg('More actions')}
          @change=${(event: Event) =>
            this.handleWorkflowOverflowAction(event, workflow)}
        >
          ${
            workflow.isTemplate
              ? nothing
              : html`<sp-menu-item value="edit">
                  <sp-icon-edit slot="icon"></sp-icon-edit>
                  ${msg('Edit automation')}
                </sp-menu-item>`
          }
          <sp-menu-item value="history">
            <sp-icon-history slot="icon"></sp-icon-history>
            ${msg('View history')}
          </sp-menu-item>
          <sp-menu-item value="delete" ?disabled=${isDeleting}>
            <sp-icon-delete slot="icon"></sp-icon-delete>
            ${msg('Delete automation')}
          </sp-menu-item>
        </sp-action-menu>
      </div>
    `;
  }

  private renderWorkflowTitle(workflow: WorkflowSummary) {
    const description = this.formatWorkflowDescription(workflow);
    return html`<div class="workflow-title-cell">
      <span class="workflow-name">${this.formatWorkflowName(workflow)}</span>
      ${
        description
          ? html`<span class="workflow-description">${description}</span>`
          : nothing
      }
      ${this.renderTriggerSummary(workflow)}
    </div>`;
  }

  private renderAttentionMeta(parts: Array<string | null | undefined>) {
    const visibleParts = parts.filter((part): part is string =>
      Boolean(part?.trim())
    );
    return html`<div class="workflow-task-meta-line">
      ${visibleParts.map(
        (part, index) => html`
          ${
            index > 0
              ? html`<span class="workflow-task-meta-dot">·</span>`
              : nothing
          }
          <span class=${part === 'Failed' ? 'status-failed' : ''}>${part}</span>
        `
      )}
    </div>`;
  }

  private renderFailedAttentionSummary(failedTaskError: { taskName: string }) {
    return html`
      <span class="workflow-task-error-summary">
        Task &ldquo;${failedTaskError.taskName}&rdquo; needs review
      </span>
      ${this.renderAttentionMeta(['Failed'])}
    `;
  }

  private renderNeedsAttentionDetail(workflow: WorkflowSummary | null) {
    if (!workflow) {
      return html`<div class="workflow-detail-empty">
        ${msg('Select an automation to see details.')}
      </div>`;
    }

    const workflowId = Number(workflow.id);
    const request = this.userInputRequests.get(workflowId);
    const failedTaskError = this.getFailedTaskError(workflow);
    const additionalFailedTaskErrors =
      this.getAdditionalFailedTaskErrorCount(workflow);
    const lastRun = this.formatNaturalDate(workflow.lastExecutedAt ?? null);
    const isFailed = workflow.lastAuditStatus === 'failed';
    const statusLabel = isFailed ? msg('Failed') : msg('Awaiting input');
    const subtitle = msg('This automation is paused until you provide input.');

    return html`<div class="workflow-detail-panel">
      <div class="workflow-detail-header">
        <div class="workflow-detail-heading">
          <h3 class="workflow-detail-title">
            ${this.formatWorkflowName(workflow)}
          </h3>
          ${this.renderAttentionMeta([statusLabel, lastRun.label])}
        </div>
        <div class="workflow-detail-actions">
          ${
            isFailed
              ? html`<sp-action-button
                  class="workflow-dismiss-button"
                  label=${msg('Dismiss failed result')}
                  @click=${() => this.requestResetWorkflow(workflow, 'dismiss')}
                >
                  ${msg('Dismiss')}
                </sp-action-button>`
              : request
                ? renderWorkflowOverviewUserInputActions({
                    renderRoot: this.renderRoot,
                    workflowId,
                    request,
                    processing: this.userInputActionTaskId === request.taskId,
                    processingAction: this.userInputActionType,
                    emitInteraction: this.emitOverviewInteraction,
                  })
                : nothing
          }
          ${
            isFailed
              ? html`<sp-action-menu
                  class="workflow-debug-menu"
                  quiet
                  label=${msg('Error support actions')}
                  @change=${(event: Event) =>
                    this.handleFailedSupportAction(event, workflow)}
                >
                  <sp-menu-item value="rerun">${msg('Rerun automation')}</sp-menu-item>
                  <sp-menu-item value="open-workflow">${msg('Open workflow')}</sp-menu-item>
                  <sp-menu-item value="view-error-details">
                    ${msg('View error details')}
                  </sp-menu-item>
                  ${
                    failedTaskError
                      ? html`<sp-menu-item value="copy-error">
                          ${msg('Copy error message')}
                        </sp-menu-item>`
                      : nothing
                  }
                </sp-action-menu>`
              : nothing
          }
        </div>
      </div>

      <div class="workflow-detail-content">
        ${
          isFailed && failedTaskError
            ? html`
              <section class="workflow-detail-section">
                <h4 class="workflow-detail-section-title">${msg('Needs review')}</h4>
                <p class="workflow-detail-section-text">
                  Technical error in step &ldquo;${failedTaskError.taskName}&rdquo;
                </p>
                ${
                  additionalFailedTaskErrors > 0
                    ? html`<p class="workflow-detail-section-meta">
                        and ${additionalFailedTaskErrors} more
                        error${additionalFailedTaskErrors === 1 ? '' : 's'}
                      </p>`
                    : nothing
                }
              </section>
              <section class="workflow-detail-section">
                <h4 class="workflow-detail-section-title">${msg('What happened')}</h4>
                <p class="workflow-detail-section-text">
                  ${msg('The automation stopped while running this step.')}
                </p>
              </section>
              <section class="workflow-detail-section">
                <h4 class="workflow-detail-section-title">${msg('Suggested action')}</h4>
                <p class="workflow-detail-section-text">
                  ${msg(
                    'Review the workflow step or retry if the underlying DATEV record was corrected.'
                  )}
                </p>
              </section>
              <section class="workflow-detail-section">
                <h4 class="workflow-detail-section-title">${msg('Advanced')}</h4>
                <details class="workflow-raw-error-disclosure">
                  <summary>${msg('Show technical details')}</summary>
                  <pre class="workflow-raw-error">${failedTaskError.error}</pre>
                </details>
              </section>
            `
            : request
              ? html`
                <p class="workflow-detail-subtitle">${subtitle}</p>
                <section class="workflow-detail-section">
                  <h4 class="workflow-detail-section-title">${msg('Awaiting input')}</h4>
                  <p class="workflow-detail-section-text">
                    ${msg(
                      'Approval required. Review the pending input and decide how to continue.'
                    )}
                  </p>
                </section>
                <section class="workflow-detail-section">
                  ${renderWorkflowOverviewUserInputPanel({
                    renderRoot: this.renderRoot,
                    workflowId,
                    request,
                    processing: this.userInputActionTaskId === request.taskId,
                    processingAction: this.userInputActionType,
                    emitInteraction: this.emitOverviewInteraction,
                    hideActions: true,
                  })}
                </section>
              `
              : nothing
        }
      </div>
    </div>`;
  }

  private renderNeedsAttentionTaskItem(workflow: WorkflowSummary) {
    const failedTaskError = this.getFailedTaskError(workflow);
    const lastRun = this.formatNaturalDate(workflow.lastExecutedAt ?? null);
    const isFailed = workflow.lastAuditStatus === 'failed';
    const selected = this.selectedAttentionWorkflowId === workflow.id;
    return html`
      <div
        class="workflow-task-item attention ${selected ? 'selected' : ''}"
        role="button"
        tabindex="0"
        aria-selected=${selected ? 'true' : 'false'}
        @click=${() => this.selectAttentionWorkflow(workflow)}
        @keydown=${(event: KeyboardEvent) => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            this.selectAttentionWorkflow(workflow);
          }
        }}
      >
        ${
          isFailed
            ? html`<span
                class="workflow-status-icon failed"
                role="img"
                aria-label=${msg('Failed')}
                title=${msg('Failed')}
              ></span>`
            : html`<span
                class="workflow-status-icon input"
                role="img"
                aria-label=${msg('Awaiting input')}
                title=${msg('Awaiting input')}
              ></span>`
        }
        <div class="workflow-task-main">
          <span class="workflow-name">${this.formatWorkflowName(workflow)}</span>
          ${
            isFailed && failedTaskError
              ? this.renderFailedAttentionSummary(failedTaskError)
              : html`
                <span class="workflow-problem-summary">
                  Approval required
                </span>
                ${this.renderAttentionMeta(['Awaiting input'])}
              `
          }
        </div>
        <span class="workflow-task-time" title=${lastRun.title}>
          ${lastRun.label}
        </span>
      </div>
    `;
  }

  private renderWorkflowTableRow(workflow: WorkflowSummary) {
    const showStatus = this.activeSection !== 'templates';
    return html`
      <sp-table-row class="workflow-table-row">
        <sp-table-cell class="workflow-table-name-cell">
          ${this.renderWorkflowTitle(workflow)}
        </sp-table-cell>
        <sp-table-cell class="workflow-table-date-cell">
          ${this.renderTableDate(workflow)}
        </sp-table-cell>
        ${
          showStatus
            ? html`<sp-table-cell class="workflow-table-status-cell">
                ${this.renderWorkflowStatusChip(workflow)}
              </sp-table-cell>`
            : nothing
        }
        <sp-table-cell class="workflow-table-actions-cell">
          ${this.renderWorkflowActionButtons(workflow, 'workflow-row-actions')}
        </sp-table-cell>
      </sp-table-row>
    `;
  }

  private renderWorkflowTableHeader() {
    const showStatus = this.activeSection !== 'templates';
    const dateLabel =
      this.activeSection === 'templates'
        ? msg('Last updated')
        : msg('Last Run');
    return html`
      <sp-table-head class="workflow-table-head">
        <sp-table-row>
          <sp-table-head-cell
            class="workflow-table-head-cell workflow-table-name-cell"
          >
            ${msg('Automation')}
          </sp-table-head-cell>
          <sp-table-head-cell
            class="workflow-table-head-cell workflow-table-date-cell"
          >
            ${dateLabel}
          </sp-table-head-cell>
          ${
            showStatus
              ? html`<sp-table-head-cell
                  class="workflow-table-head-cell workflow-table-status-cell"
                >
                  ${msg('Last Status')}
                </sp-table-head-cell>`
              : nothing
          }
          <sp-table-head-cell
            class="workflow-table-head-cell workflow-table-actions-cell"
          >
            ${msg('Actions')}
          </sp-table-head-cell>
        </sp-table-row>
      </sp-table-head>
    `;
  }

  private renderWorkflowTable(workflows: WorkflowSummary[]) {
    const tableClass =
      this.activeSection === 'templates'
        ? 'workflow-table templates'
        : 'workflow-table with-status';
    return html`
      <div class="workflow-table-wrap">
        <sp-table class=${tableClass} quiet size="s">
          ${this.renderWorkflowTableHeader()}
          <sp-table-body>
            ${workflows.map((workflow) => this.renderWorkflowTableRow(workflow))}
          </sp-table-body>
        </sp-table>
      </div>
    `;
  }

  private renderWorkflowTaskGroup(
    kind: 'awaitingInput' | 'needsReview',
    workflows: WorkflowSummary[]
  ) {
    if (!workflows.length) return nothing;
    const label =
      kind === 'awaitingInput' ? msg('Awaiting input') : msg('Needs review');
    const description =
      kind === 'awaitingInput'
        ? workflows.length === 1
          ? msg('1 automation needs approval')
          : msg(str`${workflows.length} automations need approval`)
        : workflows.length === 1
          ? msg('1 automation stopped during execution')
          : msg(str`${workflows.length} automations stopped during execution`);
    return html`
      <section class="workflow-task-group" aria-label=${label}>
        <div class="workflow-task-group-header">
          <h3 class="workflow-task-group-title">${label}</h3>
          <p class="workflow-task-group-description">${description}</p>
        </div>
        ${workflows.map((workflow) =>
          this.renderNeedsAttentionTaskItem(workflow)
        )}
      </section>
    `;
  }

  private renderNeedsAttentionContent(workflows: WorkflowSummary[]) {
    const selectedWorkflow =
      workflows.find(
        (workflow) => workflow.id === this.selectedAttentionWorkflowId
      ) ??
      workflows[0] ??
      null;

    return html`<div class="workflow-attention-layout">
      <div class="workflow-attention-list">
        <div class="workflow-task-list">
          ${this.renderWorkflowTaskGroup(
            'awaitingInput',
            workflows.filter(
              (workflow) =>
                workflow.lastAuditStatus !== 'failed' &&
                this.workflowRequiresUserInput(workflow)
            )
          )}
          ${this.renderWorkflowTaskGroup(
            'needsReview',
            workflows.filter(
              (workflow) => workflow.lastAuditStatus === 'failed'
            )
          )}
        </div>
      </div>
      <aside class="workflow-attention-detail">
        <h2 class="workflow-detail-area-heading">${msg('Details')}</h2>
        ${this.renderNeedsAttentionDetail(selectedWorkflow)}
      </aside>
    </div>`;
  }

  private renderSortControl() {
    return html`
      <sp-action-menu
        class="workflow-sort-menu"
        quiet
        size="s"
        label=${this.sortControlLabel}
        .value=${this.sortControlValue}
        @change=${this.onSortMenuChange}
      >
        <span slot="label-only">${this.sortControlLabel}</span>
        <sp-menu-item value="lastExecutedAt${MOBILE_SORT_VALUE_SEP}desc">
          ${msg('Recent first')}
        </sp-menu-item>
        <sp-menu-item value="lastExecutedAt${MOBILE_SORT_VALUE_SEP}asc">
          ${msg('Oldest first')}
        </sp-menu-item>
        <sp-menu-item value="name${MOBILE_SORT_VALUE_SEP}asc">${msg('Name A-Z')}</sp-menu-item>
        <sp-menu-item value="name${MOBILE_SORT_VALUE_SEP}desc">${msg('Name Z-A')}</sp-menu-item>
      </sp-action-menu>
    `;
  }

  private renderTaskList() {
    const workflows = this.sortedWorkflows;
    return html`
      ${
        this.loading
          ? html`<div class="state-row">
              <sp-progress-circle
                label=${msg('Loading automations')}
                indeterminate
              ></sp-progress-circle>
            </div>`
          : workflows.length === 0
            ? this.renderWorkflowEmptyState()
            : this.activeSection === 'needsAttention'
              ? this.renderNeedsAttentionContent(workflows)
              : this.renderWorkflowTable(workflows)
      }
    `;
  }

  private renderActiveSectionContent() {
    if (this.activeSection === 'overview') {
      return this.renderOverviewContent();
    }
    if (this.activeSection === 'history') {
      return html`<div class="history-wrap">
        <kv-module-audit
          compact
          .searchTerm=${this.historySearchTerm}
          @audit-filter-state-change=${this.onAuditFilterStateChange}
          @audit-summary-state-change=${this.onAuditSummaryStateChange}
        ></kv-module-audit>
      </div>`;
    }
    return this.renderTaskList();
  }

  private getResetDialogBody(pending: PendingWorkflowReset) {
    const name = this.formatWorkflowName(pending.workflow);
    return msg(
      str`Stop "${name}"? The current run and pending input will be cleared.`
    );
  }

  private get overviewReviewItems() {
    return (this.workflows ?? [])
      .filter((workflow) => this.workflowNeedsAttention(workflow))
      .slice(0, 3);
  }

  private formatOverviewDuration(durationMs?: number | null) {
    const value = formatDuration(durationMs);
    if (value === '—') return value;
    const parts = value.split(' ').filter((part) => !part.endsWith('ms'));
    if (parts.length === 0) return '0s';
    return parts.slice(0, 2).join(' ');
  }

  private openOverviewReviewItem = (
    event: Event,
    workflow: WorkflowSummary
  ) => {
    event.stopPropagation();
    this.selectAttentionWorkflow(workflow);
    this.selectSection('needsAttention');
  };

  private renderOverviewLowerCards() {
    const reviewItems = this.overviewReviewItems;
    return html`
      <div class="automation-overview-lower">
        <section class="automation-overview-list-card" aria-label=${msg('To review')}>
          <div class="automation-overview-list-header">
            <h3 class="automation-overview-section-title">${msg('To review')}</h3>
            <p class="automation-overview-section-subtitle">
              ${msg('Items waiting for a human decision.')}
            </p>
          </div>
          ${
            reviewItems.length > 0
              ? html`<div class="automation-overview-list">
                  ${reviewItems.map((workflow) => {
                    const needsInput =
                      workflow.lastAuditStatus !== 'failed' &&
                      this.workflowRequiresUserInput(workflow);
                    const status = needsInput
                      ? msg('Waiting for input')
                      : msg('Waiting for review');
                    const actionLabel = needsInput
                      ? msg('Continue')
                      : msg('Review');
                    return html`
                      <div class="automation-overview-list-row">
                        <div class="automation-overview-list-main">
                          <span class="automation-overview-list-name">
                            ${this.formatWorkflowName(workflow)}
                          </span>
                          <span class="automation-overview-list-meta">
                            ${status}
                          </span>
                        </div>
                        <sp-action-button
                          class="automation-overview-card-action"
                          quiet
                          size="s"
                          label=${actionLabel}
                          @click=${(event: Event) =>
                            this.openOverviewReviewItem(event, workflow)}
                        >
                          ${actionLabel}
                        </sp-action-button>
                      </div>
                    `;
                  })}
                </div>`
              : html`<div class="automation-overview-empty">
                  <span>${msg('No items need review.')}</span>
                </div>`
          }
        </section>
        <section class="automation-overview-list-card" aria-label=${msg('Recent work')}>
          <div class="automation-overview-list-header">
            <h3 class="automation-overview-section-title">${msg('Recent work')}</h3>
            <p class="automation-overview-section-subtitle">
              ${msg('Latest completed automation runs.')}
            </p>
          </div>
          ${
            this.recentCompletedAudits.length > 0
              ? html`<div class="automation-overview-list">
                  ${this.recentCompletedAudits.map((audit) => {
                    const completedAt = this.formatNaturalDate(
                      audit.finishedAt
                    );
                    return html`
                      <div class="automation-overview-list-row">
                        <div class="automation-overview-list-main">
                          <span class="automation-overview-list-name">
                            ${audit.workflowName}
                          </span>
                          <span class="automation-overview-list-meta">
                            ${msg('Completed in')}
                            ${this.formatOverviewDuration(audit.durationMs)}
                          </span>
                        </div>
                        <div class="automation-overview-list-side">
                          <span
                            class="automation-overview-list-time"
                            title=${completedAt.title}
                          >
                            ${completedAt.label}
                          </span>
                        </div>
                      </div>
                    `;
                  })}
                </div>`
              : html`<div class="automation-overview-empty">
                  <span>${msg('No completed work yet.')}</span>
                </div>`
          }
        </section>
      </div>
    `;
  }

  private renderImpactSection() {
    const impact = this.overviewImpact;
    return html`
      <section class="automation-impact-card" aria-label=${msg('Impact this month')}>
        <div class="automation-overview-list-header">
          <h3 class="automation-overview-section-title">${msg('Impact this month')}</h3>
          <p class="automation-overview-section-subtitle">
            ${msg('Based on automation runs this month and current setup.')}
          </p>
        </div>
        <div class="automation-impact-grid">
          <div class="automation-impact-item">
            <span class="automation-impact-value">
              ${impact.completedRuns}
            </span>
            <span class="automation-impact-label">
              ${msg('automation runs completed')}
            </span>
          </div>
          <div class="automation-impact-item">
            <span class="automation-impact-value">${impact.manualStarts}</span>
            <span class="automation-impact-label">${msg('manual starts')}</span>
          </div>
          <div class="automation-impact-item">
            <span class="automation-impact-value">${this.templateCount}</span>
            <span class="automation-impact-label">${msg('templates available')}</span>
          </div>
          <div class="automation-impact-item statement">
            <span class="automation-impact-value statement">
              ${msg('Manual work reduced')}
            </span>
            <span class="automation-impact-label">
              ${msg('Based on completed automation runs.')}
            </span>
          </div>
        </div>
      </section>
    `;
  }

  private renderOverviewContent() {
    const kpis = this.overviewKpis;
    const lastExecution = this.formatNaturalDate(
      this.latestWorkflowExecutionAt
    );
    const lastExecutionInlineLabel = `${lastExecution.label
      .charAt(0)
      .toLowerCase()}${lastExecution.label.slice(1)}`;
    return html`
      <div class="automation-overview">
        <section class="automation-cockpit-card" aria-label=${msg('Automation cockpit')}>
          <div class="automation-cockpit-copy">
            <h2 class="automation-cockpit-title">${msg('Your automation cockpit')}</h2>
            <p class="automation-cockpit-summary">
              ${this.formatAutomationCount(this.availableCount)} ${msg('are active')}.
              <span class="automation-cockpit-badge">
                ${this.needsAttentionCount}
                ${
                  this.needsAttentionCount === 1
                    ? msg('item waiting')
                    : msg('items waiting')
                }
              </span>
            </p>
            ${
              this.needsAttentionCount > 0
                ? html`<div class="automation-cockpit-actions">
                    <button
                      type="button"
                      class="automation-cockpit-review-button"
                      @click=${() => this.selectSection('needsAttention')}
                    >
                      ${msg('Review items')}
                    </button>
                  </div>`
                : nothing
            }
          </div>
          <div class="automation-cockpit-support">
            <div class="automation-cockpit-text-panel">
              <p class="automation-cockpit-message">
                ${msg('Nothing is lost. Klarvos shows every item that needs a human decision.')}
              </p>
              <p class="automation-cockpit-message">
                ${msg(
                  'Every run is visible, so your team can review exceptions without checking technical logs.'
                )}
              </p>
              ${
                this.latestWorkflowExecutionAt
                  ? html`<span
                      class="automation-cockpit-checked"
                      title=${lastExecution.title}
                    >
                      ${msg('Last automation ran')} ${lastExecutionInlineLabel}
                    </span>`
                  : html`<span class="automation-cockpit-checked">
                      ${msg('No automation runs yet')}
                    </span>`
              }
            </div>
          </div>
        </section>
        <section class="automation-overview-panel" aria-label=${msg('Automation KPIs')}>
          <div class="automation-overview-grid">
            <div class="automation-overview-card">
              <span class="automation-overview-pill running">${msg('Active')}</span>
              <span class="automation-overview-value">${this.availableCount}</span>
              <span class="automation-overview-label">${msg('Active automations')}</span>
            </div>
            <div class="automation-overview-card">
              <span class="automation-overview-pill completed">${msg('Completed')}</span>
              <span class="automation-overview-value">${kpis.completed}</span>
              <span class="automation-overview-label">${msg('Completed work')}</span>
            </div>
            <div class="automation-overview-card">
              <span class="automation-overview-pill review">${msg('To review')}</span>
              <span class="automation-overview-value">${kpis.failed}</span>
              <span class="automation-overview-label">${msg('To review')}</span>
            </div>
            <div class="automation-overview-card">
              <span class="automation-overview-pill waiting">
                ${msg('Waiting input')}
              </span>
              <span class="automation-overview-value">
                ${kpis.awaitingInput}
              </span>
              <span class="automation-overview-label">${msg('Waiting input')}</span>
            </div>
          </div>
        </section>
        ${this.renderOverviewLowerCards()}
        ${this.renderImpactSection()}
      </div>
    `;
  }

  render() {
    return html`
      <div class="overview">
        <div class="overview-shell">
          <div class="overview-header">
            <div>
              <h1 class="overview-title">
                ${msg('Automations', { id: 'workflowOverviewTitle' })}
              </h1>
              <p class="overview-description">
                ${msg('Create, run, and review automations.')}
              </p>
            </div>
            <div class="overview-actions">
              ${
                this.activeSection === 'overview' ||
                this.activeSection === 'needsAttention' ||
                this.activeSection === 'history'
                  ? nothing
                  : html`<sp-action-button
                      class="create-button"
                      ?disabled=${this.createLoading}
                      @click=${this.createWorkflow}
                      data-testid="overview-new-workflow"
                    >
                      <sp-icon-add slot="icon"></sp-icon-add>
                      ${msg('New automation')}
                    </sp-action-button>`
              }
            </div>
          </div>
          ${
            this.errorMessage
              ? html`<div class="error-panel">${this.errorMessage}</div>`
              : nothing
          }
          <section class="workflow-surface" aria-label=${msg('Automation list')}>
            ${this.renderSectionTabs()}
            ${this.renderActiveSectionContent()}
          </section>
        </div>
      </div>
      ${
        this.pendingDelete
          ? html`
            <kv-confirmation-dialog
              heading=${msg('Delete automation')}
              confirm-label=${msg('Delete')}
              @cancel=${this.cancelDelete}
              @confirm=${this.confirmDelete}
            >
              <p>
                ${msg(
                  str`Delete "${this.formatWorkflowName(this.pendingDelete)}"? This action cannot be undone.`
                )}
              </p>
            </kv-confirmation-dialog>
          `
          : nothing
      }
      ${
        this.pendingReset
          ? html`
            <kv-confirmation-dialog
              heading=${msg('Stop automation')}
              confirm-label=${msg('Stop')}
              @cancel=${this.cancelReset}
              @confirm=${this.confirmReset}
            >
              <p>${this.getResetDialogBody(this.pendingReset)}</p>
            </kv-confirmation-dialog>
          `
          : nothing
      }
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'kv-workflow-overview': KvWorkflowOverview;
  }
}
